import React, {
  ReactNode,
  useEffect,
  useRef,
  useState,
  forwardRef,
} from 'react';
import {
  Box,
  Fade,
  IconButton,
  Tooltip,
} from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { styled, keyframes, SxProps } from '@mui/material/styles';

// Bounce animation
const bounceAnimation = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
`;

const ScrollIndicatorContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 20,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  zIndex: 10,
  pointerEvents: 'none',
}));

const ScrollIndicatorButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  animation: `${bounceAnimation} 2s infinite`,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  pointerEvents: 'auto',
  boxShadow: theme.shadows[4],
}));

interface ScrollIndicatorWrapperProps {
  children: ReactNode;
  indicatorText?: string;
  showThreshold?: number;
  className?: string;
  sx?: SxProps;
  contentKey?: string | number; // prop to detect content changes
}

const ScrollIndicatorWrapper = forwardRef<HTMLDivElement, ScrollIndicatorWrapperProps>(
  (
    {
      children,
      indicatorText = 'Scroll To See Content',
      showThreshold = 30,
      className,
      sx,
      contentKey,
    },
    forwardedRef
  ) => {
    const internalRef = useRef<HTMLDivElement>(null);
    const containerRef = internalRef;

    const [showIndicator, setShowIndicator] = useState(false);
    const [hasScrolled, setHasScrolled] = useState(false);

    // Merge internal and forwarded refs
    useEffect(() => {
      const node = internalRef.current;
      if (!node || !forwardedRef) return;

      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else {
        (forwardedRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    }, [forwardedRef]);

    // Reset state when contentKey changes
    useEffect(() => {
      setHasScrolled(false);
      setShowIndicator(false);
    }, [contentKey]);

    // Check scrollability
    useEffect(() => {
      const checkScrollable = () => {
        const container = containerRef.current;
        if (container) {
          const isScrollable = container.scrollHeight > container.clientHeight;
          setShowIndicator(isScrollable && !hasScrolled);
        }
      };

      checkScrollable();
      window.addEventListener('resize', checkScrollable);
      return () => window.removeEventListener('resize', checkScrollable);
    }, [hasScrolled, contentKey]); // Added contentKey as dependency

    // Handle scroll event
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const handleScroll = () => {
        if (container.scrollTop > showThreshold) {
          setHasScrolled(true);
          setShowIndicator(false);
        }
      };

      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }, [showThreshold]);

    const handleIndicatorClick = () => {
      const container = containerRef.current;
      if (container) {
        container.scrollBy({
          top: 200,
          behavior: 'smooth',
        });
      }
    };

    return (
      <Box
        ref={containerRef}
        className={className}
        sx={{
          position: 'relative',
          overflow: 'auto',
          ...sx,
        }}
      >
        {children}
        <Fade in={showIndicator} timeout={300}>
          <ScrollIndicatorContainer>
            <Tooltip title={indicatorText}>
              <ScrollIndicatorButton
                size="small"
                onClick={handleIndicatorClick}
                aria-label="Scroll down to view more content"
              >
                <KeyboardArrowDown />
              </ScrollIndicatorButton>
            </Tooltip>
          </ScrollIndicatorContainer>
        </Fade>
      </Box>
    );
  }
);

export default ScrollIndicatorWrapper;
